import { EventBus } from "../core/event-bus"
import { EVENT } from "../core/keys"
import { getExpToNextLevel } from "../data/constants"
import type { GameData } from "../types"

/**
 * Manages run-session economy: XP, levelling, between-wave passives, and
 * material collection.  Operates on a GameData reference owned by WaveScene
 * so the scene stays the single owner of mutable state.
 */
export class EconomySystem {
  /** Levels that still need an upgrade-choice prompt. */
  readonly pendingLevelUpChoices: number[] = []
  private readonly data: GameData

  constructor(data: GameData) {
    this.data = data
  }

  // ==================== COINS ====================

  /**
   * Awards coins (with coin multiplier passive from Gold Tooth / Treasure Map).
   * Returns true when the passive triggered a duplicate.
   */
  collectCoin(value: number): boolean {
    const multiplier = 1 + (this.data.player.passives.coinMultiplier ?? 0)
    const intValue = Math.floor(value * multiplier)
    this.data.coins += intValue
    this.data.coinsThisWave += intValue
    EventBus.emit(EVENT.COIN_COLLECTED, intValue)
    return false
  }

  // ==================== LEVELING ====================

  /** Grants XP (with exp multiplier passive from EXP Crystal / Scholar's Tome) and processes any resulting level-ups. Returns true if a level was gained. */
  grantXp(value: number): boolean {
    const multiplier = 1 + (this.data.player.passives.expMultiplier ?? 0)
    this.data.player.exp += Math.floor(value * multiplier)
    return this.processLevelUps()
  }

  private processLevelUps(): boolean {
    let leveledUp = false
    let expNeeded = getExpToNextLevel(this.data.player.level)

    while (this.data.player.exp >= expNeeded) {
      this.data.player.exp -= expNeeded
      this.data.player.level += 1
      this.data.player.expToLevel = getExpToNextLevel(this.data.player.level)

      this.queueLevelUpChoiceIfNeeded()

      leveledUp = true
      expNeeded = getExpToNextLevel(this.data.player.level)

      EventBus.emit(EVENT.PLAYER_LEVELUP, this.data.player.level)
    }

    return leveledUp
  }

  private queueLevelUpChoiceIfNeeded(): void {
    this.pendingLevelUpChoices.push(this.data.player.level)
  }

  // ==================== BETWEEN-WAVE PASSIVES ====================

  applyBetweenWavePassives(): void {
    // Recover 20% of Max HP between waves
    const healAmount = Math.floor(this.data.player.maxHp * 0.2)
    this.data.player.hp = Math.min(
      this.data.player.maxHp,
      this.data.player.hp + healAmount,
    )

    this.data.player.waveGuardCharges =
      this.data.player.passives.waveGuardPerWave

    // Initialize reroll charges from passives (e.g. Dice Bag)
    this.data.player.rerollCharges =
      this.data.player.passives.rerollCharges || 0
  }
}
